<template>
  <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
    <section class="rounded-lg border bg-card p-6">
      <div class="flex flex-col gap-1">
        <h1 class="text-lg font-semibold">
          Uploadable Demo
        </h1>
        <p class="text-sm text-muted-foreground">
          Upload single or multiple files, then manage them from the list below.
        </p>
      </div>

      <div class="mt-5 grid gap-4 md:grid-cols-2">
        <label class="inline-flex items-center gap-2 text-sm">
          <input v-model="multiple" type="checkbox" class="h-4 w-4">
          <span>Allow multiple files</span>
        </label>

        <label class="inline-flex items-center gap-2 text-sm">
          <input v-model="imagesOnly" type="checkbox" class="h-4 w-4">
          <span>Images only</span>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">File Types</span>
          <input
            v-model="fileTypesInput"
            type="text"
            class="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm"
            placeholder=".pdf,image/*,application/pdf"
          >
          <span class="text-xs text-muted-foreground">
            Comma-separated values. Ignored when images-only is enabled.
          </span>
        </label>

        <div class="grid gap-3">
          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium">uploadable_id (optional)</span>
            <input
              v-model="uploadableId"
              type="text"
              class="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm"
              placeholder="e.g. 42"
            >
          </label>

          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium">uploadable_type (optional)</span>
            <input
              v-model="uploadableType"
              type="text"
              class="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm"
              placeholder="App\\Models\\Task"
            >
          </label>
        </div>
      </div>

      <div class="mt-5 flex flex-wrap items-center gap-3">
        <UploadButton
          :multiple="multiple"
          :images-only="imagesOnly"
          :file-types="parsedFileTypes"
          :uploadable-id="normalizedUploadableId"
          :uploadable-type="normalizedUploadableType"
          :disabled="isLoadingUploads"
          :label="multiple ? 'Upload files' : 'Upload file'"
          @upload-start="handleUploadStart"
          @uploaded="handleUploaded"
          @error="handleUploadError"
        />

        <button
          class="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          :disabled="isLoadingUploads"
          @click="fetchUploads"
        >
          {{ isLoadingUploads ? 'Refreshing...' : 'Refresh list' }}
        </button>

        <p v-if="statusMessage" class="text-sm text-muted-foreground">
          {{ statusMessage }}
        </p>
      </div>
    </section>

    <section class="rounded-lg border bg-card p-6">
      <div class="mb-4 flex items-center justify-between gap-3">
        <h2 class="text-base font-semibold">
          Uploaded Files
        </h2>
        <span class="text-xs text-muted-foreground">
          {{ uploads.length }} file(s)
        </span>
      </div>

      <p v-if="isLoadingUploads" class="text-sm text-muted-foreground">
        Loading uploads...
      </p>

      <p v-else-if="uploads.length === 0" class="text-sm text-muted-foreground">
        No uploaded files yet.
      </p>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="upload in uploads"
          :key="upload.id"
          class="flex flex-wrap items-center gap-2"
        >
          <UploadedFile
            :file="upload"
            @deleted="handleDeleted"
            @error="handleDeleteError"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onMounted, ref } from 'vue'

const uploads = ref([])
const multiple = ref(false)
const imagesOnly = ref(false)
const fileTypesInput = ref('')
const uploadableId = ref('')
const uploadableType = ref('')
const isLoadingUploads = ref(false)
const statusMessage = ref('')

const normalizedUploadableId = computed(() => {
  const value = uploadableId.value.trim()
  return value === '' ? null : value
})

const normalizedUploadableType = computed(() => {
  const value = uploadableType.value.trim()
  return value === '' ? null : value
})

const parsedFileTypes = computed(() => {
  const value = fileTypesInput.value.trim()
  if (value === '') {
    return []
  }

  return value
    .split(',')
    .map((fileType) => fileType.trim())
    .filter((fileType) => fileType !== '')
})

const resolveErrorMessage = (error, fallbackMessage) => {
  if (typeof error?.message === 'string' && error.message !== '') {
    return error.message
  }

  if (typeof error?.response?.data?.message === 'string' && error.response.data.message !== '') {
    return error.response.data.message
  }

  return fallbackMessage
}

const sortUploads = (items) => {
  return [...items].sort((left, right) => {
    return Number(right?.id ?? 0) - Number(left?.id ?? 0)
  })
}

const fetchUploads = async () => {
  isLoadingUploads.value = true

  try {
    const params = {
      not_paginated: true,
      order_by: 'id',
      order_direction: 'desc',
    }

    if (normalizedUploadableId.value !== null && normalizedUploadableType.value !== null) {
      params.uploadable_id = normalizedUploadableId.value
      params.uploadable_type = normalizedUploadableType.value
    }

    const response = await axios.get('/uploads', { params })
    uploads.value = sortUploads(Array.isArray(response.data) ? response.data : [])
  } catch (error) {
    statusMessage.value = resolveErrorMessage(error, 'Failed to load uploads.')
  } finally {
    isLoadingUploads.value = false
  }
}

const handleUploadStart = () => {
  statusMessage.value = 'Uploading file(s)...'
}

const handleUploaded = async (payload) => {
  const uploadedItems = Array.isArray(payload) ? payload : [payload]
  statusMessage.value = `Upload completed: ${uploadedItems.length} file(s).`
  await fetchUploads()
}

const handleUploadError = (error) => {
  statusMessage.value = resolveErrorMessage(error, 'Unable to upload selected file(s).')
}

const handleDeleted = (deletedFile) => {
  uploads.value = uploads.value.filter((upload) => upload.id !== deletedFile?.id)
  statusMessage.value = `Deleted: ${deletedFile?.filename ?? 'file'}.`
}

const handleDeleteError = (error) => {
  statusMessage.value = resolveErrorMessage(error, 'Unable to delete file.')
}

onMounted(async () => {
  await fetchUploads()
})
</script>
